// Mock data modeled after real Bright Data discovery results
// Best Buy = catalog source, Amazon = model number enrichment

export interface Variant {
  id: string;
  variantValues: Record<string, string | number>;
  variantLabel: string; // pre-formatted display, e.g. '65"'
  modelNumber: string | null; // from Amazon PDP — null if not yet enriched
  upc: string | null;
  msrp: number | null; // initial_price from Best Buy
  currentPrice: number | null; // final_price from Best Buy
  isActive: boolean;
}

export interface PriceHistory {
  low30d: number | null;
  low60d: number | null;
  low90d: number | null;
  high30d: number | null;
  priceAt30d: number | null; // price exactly 30 days ago (for trend calc)
}

export interface RetailerMatch {
  retailerId: string;
  retailerName: string;
  retailerDomain: string;
  status: "matched" | "not_found" | "pending" | "manual" | "confirmed_missing";
  productId: string | null;
  url: string | null;
  currentPrice: number | null;
  lastScrapedAt: string | null;
  consecutiveFailures: number;
  priceHistory: PriceHistory | null;
}

export interface VariantRetailerMap {
  variantId: string;
  variantValues: Record<string, string | number>;
  variantLabel: string;
  retailers: RetailerMatch[];
}

export interface ProductLine {
  id: string;
  brandName: string;
  brandDomain: string;
  categoryId: string;
  categoryName: string;
  name: string; // e.g. "Samsung S95F"
  series: string; // e.g. "S95F"
  year: number;
  displayTechnology: string;
  description: string;
  imageUrl: string | null;
  isActive: boolean;
  discoveredFrom: "bestbuy" | "amazon" | "manual";
  enrichmentStatus: "complete" | "partial" | "pending";
  variants: Variant[];
  retailerMap: VariantRetailerMap[];
}

// --- Retailers ---

export const RETAILERS = [
  { id: "r1", name: "Best Buy", domain: "bestbuy.com" },
  { id: "r2", name: "Amazon", domain: "amazon.com" },
  { id: "r3", name: "Walmart", domain: "walmart.com" },
  { id: "r4", name: "Target", domain: "target.com" },
  { id: "r5", name: "Costco", domain: "costco.com" },
  { id: "r6", name: "Samsung.com", domain: "samsung.com" },
];

function scraped(hoursAgo = 6): { lastScrapedAt: string; consecutiveFailures: number } {
  const d = new Date();
  d.setHours(d.getHours() - hoursAgo);
  return { lastScrapedAt: d.toISOString(), consecutiveFailures: 0 };
}

const NEVER = { lastScrapedAt: null, consecutiveFailures: 0 };

// Generate realistic price history relative to current price
function mockHistory(currentPrice: number | null): PriceHistory | null {
  if (currentPrice === null) return null;
  // Simulate: price was higher 30 days ago, with some drops
  const p = currentPrice;
  return {
    low30d: Math.round((p * 0.97) * 100) / 100, // 3% below current
    low60d: Math.round((p * 0.93) * 100) / 100, // 7% below
    low90d: Math.round((p * 0.90) * 100) / 100, // 10% below
    high30d: Math.round((p * 1.05) * 100) / 100,
    priceAt30d: Math.round((p * 1.08) * 100) / 100, // was 8% higher 30d ago
  };
}

function rm(
  retailerIdx: number,
  status: RetailerMatch["status"],
  opts: { pid?: string; url?: string; price?: number; trend?: "up" | "down" | "flat" | "new_low" } = {},
): RetailerMatch {
  const r = RETAILERS[retailerIdx];
  const price = opts.price ?? null;

  // Vary the history based on trend hint
  let history: PriceHistory | null = null;
  if (price !== null && status === "matched") {
    const base = mockHistory(price)!;
    switch (opts.trend) {
      case "up": // price went up recently
        base.priceAt30d = Math.round(price * 0.92 * 100) / 100;
        base.low30d = Math.round(price * 0.90 * 100) / 100;
        break;
      case "new_low": // current price IS the 30/60/90 day low
        base.low30d = price;
        base.low60d = price;
        base.priceAt30d = Math.round(price * 1.15 * 100) / 100;
        break;
      case "flat":
        base.priceAt30d = price;
        base.low30d = price;
        break;
      default: // "down" — default mock behavior (price dropped)
        break;
    }
    history = base;
  }

  return {
    retailerId: r.id,
    retailerName: r.name,
    retailerDomain: r.domain,
    status,
    productId: opts.pid ?? null,
    url: opts.url ?? null,
    currentPrice: price,
    priceHistory: history,
    ...(status === "matched" ? scraped() : NEVER),
  };
}

// === Samsung S95F — flagship QD-OLED ===
const samsungS95F: ProductLine = {
  id: "pl1",
  brandName: "Samsung",
  brandDomain: "samsung.com",
  categoryId: "cat1",
  categoryName: "OLED TVs",
  name: "Samsung S95F",
  series: "S95F",
  year: 2025,
  displayTechnology: "QD-OLED",
  description: "Samsung's flagship 4K OLED with Vision AI, Glare Free, and 165Hz motion.",
  imageUrl: "https://pisces.bbystatic.com/image2/BestBuy_US/images/products/6613/6613459_sd.jpg",
  isActive: true,
  discoveredFrom: "bestbuy",
  enrichmentStatus: "partial",
  variants: [
    { id: "v1", variantValues: { screen_size_inches: 55 }, variantLabel: '55"', modelNumber: null, upc: null, msrp: 2499.99, currentPrice: null, isActive: true },
    { id: "v2", variantValues: { screen_size_inches: 65 }, variantLabel: '65"', modelNumber: "QN65S95FAFXZA", upc: null, msrp: 3099.99, currentPrice: 2799.99, isActive: true },
    { id: "v3", variantValues: { screen_size_inches: 77 }, variantLabel: '77"', modelNumber: "QN77S95FAFXZA", upc: null, msrp: 4499.99, currentPrice: 3299.99, isActive: true },
    { id: "v4", variantValues: { screen_size_inches: 83 }, variantLabel: '83"', modelNumber: null, upc: null, msrp: 5499.99, currentPrice: 4999.99, isActive: true },
  ],
  retailerMap: [
    {
      variantId: "v1", variantValues: { screen_size_inches: 55 }, variantLabel: '55"',
      retailers: [
        rm(0, "not_found"),
        rm(1, "not_found"),
        rm(2, "not_found"),
        rm(3, "matched", { pid: "A-91234567", url: "https://www.target.com/p/-/A-91234567", price: 2499.99, trend: "flat" }),
        rm(4, "pending"),
        rm(5, "pending"),
      ],
    },
    {
      variantId: "v2", variantValues: { screen_size_inches: 65 }, variantLabel: '65"',
      retailers: [
        rm(0, "not_found"),
        rm(1, "matched", { pid: "B0DXYZ65S9", url: "https://www.amazon.com/dp/B0DXYZ65S9", price: 2799.99, trend: "down" }),
        rm(2, "not_found"),
        rm(3, "not_found"),
        rm(4, "pending"),
        rm(5, "pending"),
      ],
    },
    {
      variantId: "v3", variantValues: { screen_size_inches: 77 }, variantLabel: '77"',
      retailers: [
        rm(0, "matched", { pid: "6613459", url: "https://www.bestbuy.com/site/6613459.p", price: 3299.99, trend: "new_low" }),
        rm(1, "matched", { pid: "B0DXYZ77S9", url: "https://www.amazon.com/dp/B0DXYZ77S9", price: 3297.99, trend: "down" }),
        rm(2, "not_found"),
        rm(3, "not_found"),
        rm(4, "matched", { pid: "4000234569", url: "https://www.costco.com/samsung-77-s95f.product.4000234569.html", price: 3149.99, trend: "new_low" }),
        rm(5, "matched", { pid: "QN77S95FAFXZA", url: "https://www.samsung.com/us/tvs/oled-tv/77-class-oled-tv-s95f-sku-qn77s95fafxza/", price: 3299.99, trend: "flat" }),
      ],
    },
    {
      variantId: "v4", variantValues: { screen_size_inches: 83 }, variantLabel: '83"',
      retailers: [
        rm(0, "matched", { pid: "6614339", url: "https://www.bestbuy.com/site/6614339.p", price: 4999.99, trend: "up" }),
        rm(1, "not_found"),
        rm(2, "not_found"),
        rm(3, "not_found"),
        rm(4, "pending"),
        rm(5, "matched", { pid: "QN83S95FAFXZA", url: "https://www.samsung.com/us/tvs/oled-tv/83-class-oled-tv-s95f-sku-qn83s95fafxza/", price: 4999.99 }),
      ],
    },
  ],
};

// === Samsung S90F — premium OLED ===
const samsungS90F: ProductLine = {
  id: "pl2",
  brandName: "Samsung",
  brandDomain: "samsung.com",
  categoryId: "cat1",
  categoryName: "OLED TVs",
  name: "Samsung S90F",
  series: "S90F",
  year: 2025,
  displayTechnology: "OLED",
  description: "Self-illuminating pixels with bright, expert-validated color and dramatic contrast.",
  imageUrl: "https://pisces.bbystatic.com/image2/BestBuy_US/images/products/6613/6613462_sd.jpg",
  isActive: true,
  discoveredFrom: "bestbuy",
  enrichmentStatus: "partial",
  variants: [
    { id: "v5", variantValues: { screen_size_inches: 42 }, variantLabel: '42"', modelNumber: null, upc: null, msrp: 1099.99, currentPrice: 899.99, isActive: true },
    { id: "v6", variantValues: { screen_size_inches: 48 }, variantLabel: '48"', modelNumber: null, upc: null, msrp: 1299.99, currentPrice: null, isActive: true },
    { id: "v7", variantValues: { screen_size_inches: 55 }, variantLabel: '55"', modelNumber: "QN55S90FAFXZA", upc: null, msrp: 1599.99, currentPrice: 1399.99, isActive: true },
    { id: "v8", variantValues: { screen_size_inches: 65 }, variantLabel: '65"', modelNumber: "QN65S90FAFXZA", upc: null, msrp: 2499.99, currentPrice: 1399.99, isActive: true },
    { id: "v9", variantValues: { screen_size_inches: 77 }, variantLabel: '77"', modelNumber: "QN77S90FAFXZA", upc: null, msrp: 3499.99, currentPrice: 1999.99, isActive: true },
    { id: "v10", variantValues: { screen_size_inches: 83 }, variantLabel: '83"', modelNumber: null, upc: null, msrp: 3799.99, currentPrice: 2999.99, isActive: true },
  ],
  retailerMap: [
    {
      variantId: "v5", variantValues: { screen_size_inches: 42 }, variantLabel: '42"',
      retailers: [
        rm(0, "not_found"), rm(1, "not_found"), rm(2, "not_found"),
        rm(3, "matched", { pid: "A-91234570", price: 899.99, trend: "new_low" }),
        rm(4, "not_found"), rm(5, "matched", { pid: "QN42S90FAFXZA", price: 899.99, trend: "flat" }),
      ],
    },
    {
      variantId: "v6", variantValues: { screen_size_inches: 48 }, variantLabel: '48"',
      retailers: [
        rm(0, "not_found"), rm(1, "not_found"),
        rm(2, "matched", { pid: "5765432200", price: 1099.99 }),
        rm(3, "not_found"), rm(4, "not_found"), rm(5, "pending"),
      ],
    },
    {
      variantId: "v7", variantValues: { screen_size_inches: 55 }, variantLabel: '55"',
      retailers: [
        rm(0, "not_found"),
        rm(1, "matched", { pid: "B0DV3GXK55", url: "https://www.amazon.com/dp/B0DV3GXK55", price: 1397.99, trend: "down" }),
        rm(2, "matched", { pid: "5765432115", price: 1399.99, trend: "up" }),
        rm(3, "matched", { pid: "A-91234572", price: 1399.99, trend: "down" }),
        rm(4, "not_found"), rm(5, "matched", { pid: "QN55S90FAFXZA", price: 1399.99, trend: "flat" }),
      ],
    },
    {
      variantId: "v8", variantValues: { screen_size_inches: 65 }, variantLabel: '65"',
      retailers: [
        rm(0, "matched", { pid: "6613491", url: "https://www.bestbuy.com/site/6613491.p", price: 1399.99, trend: "new_low" }),
        rm(1, "matched", { pid: "B0DV3GXK65", url: "https://www.amazon.com/dp/B0DV3GXK65", price: 1397.99, trend: "new_low" }),
        rm(2, "matched", { pid: "5765432116", price: 1699.99, trend: "flat" }),
        rm(3, "matched", { pid: "A-91234573", price: 1699.99, trend: "up" }),
        rm(4, "matched", { pid: "4000234570", price: 1599.99, trend: "down" }),
        rm(5, "matched", { pid: "QN65S90FAFXZA", price: 1699.99, trend: "flat" }),
      ],
    },
    {
      variantId: "v9", variantValues: { screen_size_inches: 77 }, variantLabel: '77"',
      retailers: [
        rm(0, "matched", { pid: "6613462", url: "https://www.bestbuy.com/site/6613462.p", price: 1999.99, trend: "down" }),
        rm(1, "matched", { pid: "B0DV3GXK77", url: "https://www.amazon.com/dp/B0DV3GXK77", price: 1997.99, trend: "down" }),
        rm(2, "matched", { pid: "5765432117", price: 1999.99, trend: "flat" }),
        rm(3, "not_found"),
        rm(4, "matched", { pid: "4000234571", price: 1849.99, trend: "new_low" }),
        rm(5, "matched", { pid: "QN77S90FAFXZA", price: 1999.99, trend: "up" }),
      ],
    },
    {
      variantId: "v10", variantValues: { screen_size_inches: 83 }, variantLabel: '83"',
      retailers: [
        rm(0, "matched", { pid: "6613461", url: "https://www.bestbuy.com/site/6613461.p", price: 2999.99 }),
        rm(1, "not_found"), rm(2, "not_found"), rm(3, "not_found"),
        rm(4, "matched", { pid: "4000234572", price: 2849.99 }),
        rm(5, "matched", { pid: "QN83S90FAFXZA", price: 2999.99 }),
      ],
    },
  ],
};

// === Samsung QN90F — Neo QLED ===
const samsungQN90F: ProductLine = {
  id: "pl3",
  brandName: "Samsung",
  brandDomain: "samsung.com",
  categoryId: "cat2",
  categoryName: "Mini-LED TVs",
  name: "Samsung QN90F",
  series: "QN90F",
  year: 2025,
  displayTechnology: "Neo QLED Mini-LED",
  description: "Neo QLED with NQ4 AI Gen3 Processor, Anti-Glare, and 144Hz motion.",
  imageUrl: null,
  isActive: true,
  discoveredFrom: "bestbuy",
  enrichmentStatus: "partial",
  variants: [
    { id: "v11", variantValues: { screen_size_inches: 55 }, variantLabel: '55"', modelNumber: "QN55QN90FAFXZA", upc: null, msrp: 1799.99, currentPrice: 1199.99, isActive: true },
    { id: "v12", variantValues: { screen_size_inches: 65 }, variantLabel: '65"', modelNumber: "QN65QN90FAFXZA", upc: null, msrp: 2499.99, currentPrice: null, isActive: true },
    { id: "v13", variantValues: { screen_size_inches: 75 }, variantLabel: '75"', modelNumber: null, upc: null, msrp: 2799.99, currentPrice: 1699.99, isActive: true },
    { id: "v14", variantValues: { screen_size_inches: 85 }, variantLabel: '85"', modelNumber: null, upc: null, msrp: 3499.99, currentPrice: 2199.99, isActive: true },
    { id: "v15", variantValues: { screen_size_inches: 98 }, variantLabel: '98"', modelNumber: null, upc: null, msrp: 9999.99, currentPrice: 8999.99, isActive: true },
  ],
  retailerMap: [
    {
      variantId: "v11", variantValues: { screen_size_inches: 55 }, variantLabel: '55"',
      retailers: [
        rm(0, "matched", { pid: "6614100", price: 1199.99 }),
        rm(1, "matched", { pid: "B0DXYZ55QN", price: 1197.99 }),
        rm(2, "matched", { pid: "5765432130", price: 1199.99 }),
        rm(3, "matched", { pid: "A-91234585", price: 1199.99 }),
        rm(4, "not_found"), rm(5, "pending"),
      ],
    },
    {
      variantId: "v12", variantValues: { screen_size_inches: 65 }, variantLabel: '65"',
      retailers: [
        rm(0, "not_found"),
        rm(1, "matched", { pid: "B0DXYZ65QN", price: 1697.99 }),
        rm(2, "not_found"),
        rm(3, "matched", { pid: "A-91234586", price: 1699.99 }),
        rm(4, "matched", { pid: "4000234580", price: 1599.99 }),
        rm(5, "pending"),
      ],
    },
    {
      variantId: "v13", variantValues: { screen_size_inches: 75 }, variantLabel: '75"',
      retailers: [
        rm(0, "matched", { pid: "6614102", price: 1699.99 }),
        rm(1, "not_found"),
        rm(2, "not_found"),
        rm(3, "not_found"),
        rm(4, "matched", { pid: "4000234581", price: 1599.99 }),
        rm(5, "pending"),
      ],
    },
    {
      variantId: "v14", variantValues: { screen_size_inches: 85 }, variantLabel: '85"',
      retailers: [
        rm(0, "matched", { pid: "6614103", price: 2199.99 }),
        rm(1, "matched", { pid: "B0DXYZ85QN", price: 2197.99 }),
        rm(2, "matched", { pid: "5765432133", price: 2199.99 }),
        rm(3, "not_found"),
        rm(4, "not_found"),
        rm(5, "pending"),
      ],
    },
    {
      variantId: "v15", variantValues: { screen_size_inches: 98 }, variantLabel: '98"',
      retailers: [
        rm(0, "matched", { pid: "6614104", price: 8999.99 }),
        rm(1, "not_found"), rm(2, "not_found"), rm(3, "not_found"), rm(4, "not_found"), rm(5, "pending"),
      ],
    },
  ],
};

// === Samsung Q8F — QLED ===
const samsungQ8F: ProductLine = {
  id: "pl4",
  brandName: "Samsung",
  brandDomain: "samsung.com",
  categoryId: "cat3",
  categoryName: "QLED TVs",
  name: "Samsung Q8F",
  series: "Q8F",
  year: 2025,
  displayTechnology: "QLED",
  description: "Quantum Dot color with Vision AI and 120Hz motion.",
  imageUrl: null,
  isActive: true,
  discoveredFrom: "bestbuy",
  enrichmentStatus: "pending",
  variants: [
    { id: "v16", variantValues: { screen_size_inches: 43 }, variantLabel: '43"', modelNumber: "QN43Q8FAAFXZA", upc: null, msrp: 599.99, currentPrice: 449.99, isActive: true },
    { id: "v17", variantValues: { screen_size_inches: 50 }, variantLabel: '50"', modelNumber: null, upc: null, msrp: 699.99, currentPrice: 499.99, isActive: true },
    { id: "v18", variantValues: { screen_size_inches: 55 }, variantLabel: '55"', modelNumber: null, upc: null, msrp: 799.99, currentPrice: 599.99, isActive: true },
    { id: "v19", variantValues: { screen_size_inches: 65 }, variantLabel: '65"', modelNumber: null, upc: null, msrp: 999.99, currentPrice: 749.99, isActive: true },
    { id: "v20", variantValues: { screen_size_inches: 75 }, variantLabel: '75"', modelNumber: null, upc: null, msrp: 1299.99, currentPrice: 899.99, isActive: true },
    { id: "v21", variantValues: { screen_size_inches: 85 }, variantLabel: '85"', modelNumber: null, upc: null, msrp: 1799.99, currentPrice: 1299.99, isActive: true },
  ],
  retailerMap: [
    { variantId: "v16", variantValues: { screen_size_inches: 43 }, variantLabel: '43"', retailers: [rm(0, "matched", { pid: "6614200", price: 449.99 }), rm(1, "matched", { pid: "B0DQ8F43", price: 447.99 }), rm(2, "not_found"), rm(3, "matched", { pid: "A-912345Q8", price: 449.99 }), rm(4, "not_found"), rm(5, "pending")] },
    { variantId: "v17", variantValues: { screen_size_inches: 50 }, variantLabel: '50"', retailers: [rm(0, "matched", { pid: "6614201", price: 499.99 }), rm(1, "not_found"), rm(2, "not_found"), rm(3, "matched", { pid: "A-912346Q8", price: 499.99 }), rm(4, "not_found"), rm(5, "pending")] },
    { variantId: "v18", variantValues: { screen_size_inches: 55 }, variantLabel: '55"', retailers: [rm(0, "matched", { pid: "6614202", price: 599.99 }), rm(1, "matched", { pid: "B0DQ8F55", price: 597.99 }), rm(2, "not_found"), rm(3, "matched", { pid: "A-912347Q8", price: 599.99 }), rm(4, "not_found"), rm(5, "pending")] },
    { variantId: "v19", variantValues: { screen_size_inches: 65 }, variantLabel: '65"', retailers: [rm(0, "matched", { pid: "6614203", price: 749.99 }), rm(1, "matched", { pid: "B0DQ8F65", price: 747.99 }), rm(2, "matched", { pid: "5765Q865", price: 749.99 }), rm(3, "matched", { pid: "A-912348Q8", price: 749.99 }), rm(4, "not_found"), rm(5, "pending")] },
    { variantId: "v20", variantValues: { screen_size_inches: 75 }, variantLabel: '75"', retailers: [rm(0, "matched", { pid: "6614204", price: 899.99 }), rm(1, "not_found"), rm(2, "not_found"), rm(3, "not_found"), rm(4, "matched", { pid: "400Q875", price: 849.99 }), rm(5, "pending")] },
    { variantId: "v21", variantValues: { screen_size_inches: 85 }, variantLabel: '85"', retailers: [rm(0, "matched", { pid: "6614205", price: 1299.99 }), rm(1, "not_found"), rm(2, "not_found"), rm(3, "not_found"), rm(4, "not_found"), rm(5, "pending")] },
  ],
};

// === Samsung U8000F — Crystal UHD (value) ===
const samsungU8000F: ProductLine = {
  id: "pl5",
  brandName: "Samsung",
  brandDomain: "samsung.com",
  categoryId: "cat4",
  categoryName: "Crystal UHD TVs",
  name: "Samsung U8000F",
  series: "U8000F",
  year: 2025,
  displayTechnology: "Crystal UHD",
  description: "Crystal Processor with PurColor, Motion Xcelerator, and Smart Hub.",
  imageUrl: null,
  isActive: true,
  discoveredFrom: "bestbuy",
  enrichmentStatus: "complete",
  variants: [
    { id: "v22", variantValues: { screen_size_inches: 43 }, variantLabel: '43"', modelNumber: "UN43U8000FXZA", upc: null, msrp: 299.99, currentPrice: 199.99, isActive: true },
    { id: "v23", variantValues: { screen_size_inches: 50 }, variantLabel: '50"', modelNumber: "UN50U8000FXZA", upc: null, msrp: 349.99, currentPrice: 249.99, isActive: true },
    { id: "v24", variantValues: { screen_size_inches: 55 }, variantLabel: '55"', modelNumber: "UN55U8000FXZA", upc: null, msrp: 399.99, currentPrice: 299.99, isActive: true },
    { id: "v25", variantValues: { screen_size_inches: 65 }, variantLabel: '65"', modelNumber: "UN65U8000FXZA", upc: null, msrp: 499.99, currentPrice: 397.99, isActive: true },
    { id: "v26", variantValues: { screen_size_inches: 75 }, variantLabel: '75"', modelNumber: "UN75U8000FXZA", upc: null, msrp: 699.99, currentPrice: 549.99, isActive: true },
    { id: "v27", variantValues: { screen_size_inches: 85 }, variantLabel: '85"', modelNumber: "UN85U8000FXZA", upc: null, msrp: 999.99, currentPrice: 799.99, isActive: true },
  ],
  retailerMap: [
    { variantId: "v22", variantValues: { screen_size_inches: 43 }, variantLabel: '43"', retailers: [rm(0, "matched", { pid: "6617400", price: 199.99 }), rm(1, "matched", { pid: "B0DXU843", price: 198.99 }), rm(2, "matched", { pid: "WM43U8", price: 199.99 }), rm(3, "matched", { pid: "A-43U8", price: 199.99 }), rm(4, "not_found"), rm(5, "pending")] },
    { variantId: "v23", variantValues: { screen_size_inches: 50 }, variantLabel: '50"', retailers: [rm(0, "matched", { pid: "6617401", price: 249.99 }), rm(1, "matched", { pid: "B0DXU850", price: 248.99 }), rm(2, "matched", { pid: "WM50U8", price: 249.99 }), rm(3, "matched", { pid: "A-50U8", price: 249.99 }), rm(4, "not_found"), rm(5, "pending")] },
    { variantId: "v24", variantValues: { screen_size_inches: 55 }, variantLabel: '55"', retailers: [rm(0, "matched", { pid: "6617402", price: 299.99 }), rm(1, "matched", { pid: "B0DXU855", price: 298.99 }), rm(2, "matched", { pid: "WM55U8", price: 379.99 }), rm(3, "matched", { pid: "A-55U8", price: 299.99 }), rm(4, "not_found"), rm(5, "pending")] },
    { variantId: "v25", variantValues: { screen_size_inches: 65 }, variantLabel: '65"', retailers: [rm(0, "matched", { pid: "6617403", price: 397.99 }), rm(1, "matched", { pid: "B0DXU865", price: 397.99 }), rm(2, "matched", { pid: "WM65U8", price: 399.99 }), rm(3, "matched", { pid: "A-65U8", price: 399.99 }), rm(4, "matched", { pid: "CO65U8", price: 379.99 }), rm(5, "pending")] },
    { variantId: "v26", variantValues: { screen_size_inches: 75 }, variantLabel: '75"', retailers: [rm(0, "matched", { pid: "6617404", price: 549.99 }), rm(1, "matched", { pid: "B0DXU875", price: 547.99 }), rm(2, "matched", { pid: "WM75U8", price: 549.99 }), rm(3, "matched", { pid: "A-75U8", price: 549.99 }), rm(4, "matched", { pid: "CO75U8", price: 529.99 }), rm(5, "pending")] },
    { variantId: "v27", variantValues: { screen_size_inches: 85 }, variantLabel: '85"', retailers: [rm(0, "matched", { pid: "6617405", price: 799.99 }), rm(1, "matched", { pid: "B0DXU885", price: 797.99 }), rm(2, "not_found"), rm(3, "matched", { pid: "A-85U8", price: 799.99 }), rm(4, "not_found"), rm(5, "pending")] },
  ],
};

export const MOCK_PRODUCT_LINES: ProductLine[] = [
  samsungS95F,
  samsungS90F,
  samsungQN90F,
  samsungQ8F,
  samsungU8000F,
];
